import { Injectable } from '@angular/core';
import { HttpService } from "./http.service";
import { MerchantDTViewModel } from "../models/merchant-dt-view-model";
import { CreateResult } from "../models/create-result";
import { MerchantViewModel } from "../models/merchant-view-model";
import { AddUpdateMerchantViewModel } from "../models/add-update-merchant-view-model"
import { CountryViewModel } from "../models/country-view-model";
import { RegionViewModel } from "../models/region-view-model";
import { MerchantTypeViewModel } from "../models/merchant-type-view-model";
import { BankViewModel } from '../models/bank-view-model';
import { AccountTypeViewModel } from '../models/account-type-view-model';
import { BankingDetailsViewModel } from '../models/banking-details-view-model';
import { UserDataStoreService } from './user-data-store.service';
import { ContactDetailsViewModel } from '../models/contact-details-view-model';
import { UpdateCommissionDetailsViewModel } from '../models/update-commission-details-view-model';
import { VASCustomerBalanceViewModel } from '../models/vas-customer-balance-view-model';
import { CardSetsViewModel } from '../models/card-set';
import { SinkNodeMappedFieldsViewModel } from '../models/sinknode-mapped-fields-view-model';
import { BehaviorSubject, Observable } from 'rxjs';
import { MerchantDetailModel } from '../models/merchant-detail-model';
import { TmCardAcceptorConfigViewModel } from '../models/tm-card-acceptor-model';
import { TaCardAcceptorConfigViewModel } from '../models/ta-card-acceptor-model';
import { PFIViewModel } from '../models/PFI-view-model';
import { ServiceModel } from '../models/service-model';
import { merchantParamsModel } from '../models/merchant-params-model';
import { PagedResponse } from '../models/paged-response';
import { SearchFilterModel } from '../models/search-filter-model';
import { ParticipantViewModel } from '../models/participant-view-model';
import { MerchantSignUpViewModel } from '../models/merchant-sign-up-view-model';
import { MerchantSignedUpViewModel } from '../models/merchants-sign-up-view-model';
import { merchantTerminalsLink } from '../models/merchant-terminals-link';
import { ParticipantCountryMappingViewModel } from '../models/participant-country-mapping-view-model';

@Injectable({
  providedIn: 'root'
})
export class MerchantService {
   
  private countSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public get count(): number {
    return this.countSubject.value;
  }

  public set count(value) {
    this.countSubject.next(value);
  }

  public get countObservable(): Observable<number> {
    return this.countSubject.asObservable();
  }

  constructor(private _httpService: HttpService, private _userDataStore: UserDataStoreService) {
    this.InitMerchantCount();
  }

  public async GetDefaultCountry(): Promise<ParticipantCountryMappingViewModel>{
    const httpResult = await this._httpService.apiGet<ParticipantCountryMappingViewModel>(`merchant/getParticipantCountryMapping`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async Get(merchantNumber: string): Promise<MerchantViewModel> {
    const httpResult = await this._httpService.apiGet<MerchantViewModel>(`merchant/${merchantNumber}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async GetAllMerchants(): Promise<MerchantDTViewModel[]> {

    const httpResult = await this._httpService.apiGet<MerchantDTViewModel[]>(`merchant/all`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetAllMerchantsPaged(filter: SearchFilterModel): Promise<PagedResponse<MerchantDTViewModel>> {
    const httpResult = await this._httpService.apiPost<PagedResponse<MerchantDTViewModel>>(`merchant/all-merchants`, filter);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return {
      response: null,
      total: 0
    };
  }

  public async GetAllMerchantsByParticipantID(participantId: number): Promise<MerchantDTViewModel[]> {

    const httpResult = await this._httpService.apiGet<MerchantDTViewModel[]>(`merchant/getAll/${participantId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetMerchantSignedUp(merchantNumber: string): Promise<MerchantSignedUpViewModel> {
    const httpResult = await this._httpService.apiGet<MerchantSignedUpViewModel>(`merchant/merchantSignUp/${merchantNumber}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async MerchantSignUp(merchant: MerchantSignedUpViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`merchant/merchantSignUp/update`, merchant);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async AddMerchantSignUp(merchant: MerchantSignedUpViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`merchant/merchantSignUp/add`, merchant);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async InitMerchantCount(): Promise<void> {
    this.countSubject = new BehaviorSubject<number>(0);
    const httpResult = await this._httpService.apiGet<number>(`merchant/count`);
    if (httpResult && httpResult.body) {
      this.count = httpResult.body;
    }
  }

  public async AddMerchantDetails(merchantDetails: MerchantDetailModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`merchant/AddMerchantDetails`, merchantDetails);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }


  public async Update(merchant: AddUpdateMerchantViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`merchant/Update`, merchant);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async DeleteMerchant(merchantNumber: string): Promise<CreateResult> {
    const httpResult = await this._httpService.apiDelete<CreateResult>(`merchant/Delete/${merchantNumber}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "User does not have the full required permissions to delete a merchant, contact system adminsitrator.", success: false };
  }

  public async UpdateVASCustomerDetailsAndVASSellerCommission(merchant: AddUpdateMerchantViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`merchant/UpdateVASCustomerDetailsAndVASSellerCommission`, merchant);
    if (httpResult && httpResult.body) {
      this.InitMerchantCount();
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  //public async GetMerchantById(merchantNumber: string): Promise<CreateResult> {
  //  const httpResult = await this._httpService.apiGet<CreateResult>(`merchant/merchantSignUp/${merchantNumber}`);
  //  if (httpResult && httpResult.body) {
  //    return { message: `Merchant ${merchantNumber} already exist`, success: false };
  //  }
  //  return { message: `Merchant ${merchantNumber} doea not exist`, success: true };
  //}

  public async Add(merchantModel: AddUpdateMerchantViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`merchant/Add`, merchantModel);
    if (httpResult && httpResult.body) {
      this.InitMerchantCount();
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async GetCardSets(): Promise<CardSetsViewModel[]> {

    const httpResult = await this._httpService.apiGet<CardSetsViewModel[]>(`merchant/cardSets`);

    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async CountCardSets(cardSetId: CardSetsViewModel): Promise<number> {
    const httpResult = await this._httpService.apiPost<number>(`merchant/countCardSets`, cardSetId);

    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return 0;
  }

  public async GetCardSetsByParticipant(id: number): Promise<CardSetsViewModel[]> {

    const httpResult = await this._httpService.apiGet<CardSetsViewModel[]>(`merchant/cardSetsByParticipant/${id}`);

    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetCardSetsByMerchant(merchantNumber: string): Promise<CardSetsViewModel[]> {

    const httpResult = await this._httpService.apiGet<CardSetsViewModel[]>(`merchant/cardSetsByMerchant/${merchantNumber}`);

    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetMerchantSettings(merchantNumber: string): Promise<ParticipantViewModel> {

    const httpResult = await this._httpService.apiGet<ParticipantViewModel>(`merchant/getMerchantSettings/${merchantNumber}`);

    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async GetCountries(): Promise<CountryViewModel[]> {
    const httpResult = await this._httpService.apiGet<CountryViewModel[]>(`merchant/countries`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetRegions(): Promise<RegionViewModel[]> {
    const httpResult = await this._httpService.apiGet<RegionViewModel[]>(`merchant/regions`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetRegionsByCountry(country: string): Promise<RegionViewModel[]>{
    const httpResult = await this._httpService.apiGet<RegionViewModel[]>(`merchant/regionsByCountry/${country}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }


  public async GetMerchantTypes(): Promise<MerchantTypeViewModel[]> {
    const httpResult = await this._httpService.apiGet<MerchantTypeViewModel[]>(`merchant/merchantTypes`);

    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetBanks(): Promise<BankViewModel[]> {
    const httpResult = await this._httpService.apiGet<BankViewModel[]>(`merchant/banks`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetAccountTypes(): Promise<AccountTypeViewModel[]> {
    const httpResult = await this._httpService.apiGet<AccountTypeViewModel[]>(`merchant/accountTypes`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetCountryDetails(countryCode: string): Promise<CountryViewModel> {
    const httpResult = await this._httpService.apiGet<CountryViewModel>(`merchant/GetCountryDetails`,
      {
        countryCode: countryCode
      });
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async GetBankingDetails(merchantNumber: string, participantId: string): Promise<BankingDetailsViewModel> {
    const httpResult = await this._httpService.apiGet<BankingDetailsViewModel>(`merchant/GetBankingDetails`,
      {
        merchantNumber: merchantNumber,
        participantId: participantId
      });
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async GetMerchantDetails(merchantNumber: string): Promise<MerchantDetailModel> {
    const httpResult = await this._httpService.apiGet<MerchantDetailModel>(`merchant/GetMerchantDetails`,
      {
        merchantNumber: merchantNumber
      });
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async GetTmCardAcceptor(merchantNumber: string): Promise<TmCardAcceptorConfigViewModel> {
    const httpResult = await this._httpService.apiGet<TmCardAcceptorConfigViewModel>(`merchant/GetTmCardAcceptor`,
      {
        merchantNumber: merchantNumber
      });
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async GetTaCardAcceptor(merchantNumber: string): Promise<TaCardAcceptorConfigViewModel> {
    const httpResult = await this._httpService.apiGet<TaCardAcceptorConfigViewModel>(`merchant/GetTaCardAcceptor`,
      {
        merchantNumber: merchantNumber
      });
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }
  public async GetMerchantServices(merchantNumber: string): Promise<ServiceModel[]> {
    const httpResult = await this._httpService.apiGet<ServiceModel[]>(`merchantParams/GetMerchantServices/${merchantNumber}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }


  public async HasMerchantService(merchantNumber: string, serviceId: number): Promise<boolean> {
    
    const httpResult = await this._httpService.apiGet<boolean>(`MerchantParams/HasMerchantService/${merchantNumber}/${serviceId}`); 
    if (httpResult.error == null){
      return httpResult.body;
    }
    return null;
  }

  public async GetMerchantParams(merchantNumber: string): Promise<merchantParamsModel[]> {
    const httpResult = await this._httpService.apiGet<merchantParamsModel[]>(`merchantParams/${merchantNumber}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async CreateMerchantParams(merchantParams: merchantParamsModel[]): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`merchantParams`, merchantParams);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async UpdateMerchantParams(merchantParams: merchantParamsModel[]): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPut<CreateResult>(`merchantParams`, merchantParams);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async updateMerchant(merchant: MerchantDetailModel): Promise<CreateResult> {
    debugger;
    const httpResult = await this._httpService.apiPut<CreateResult>(`merchant/UpdateMerchantDetails`, merchant);
    debugger;
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async DeleteMerchantParam(parameterId: number): Promise<CreateResult> {
    const httpResult = await this._httpService.apiDelete<CreateResult>(`merchantParams/${parameterId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async UpdateBankingDetails(bankingDetails: BankingDetailsViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`merchant/UpdateBankingDetails`, bankingDetails);
    //const httpResult = await this._httpService.apiPost<CreateResult>(`merchant/${this._userDataStore.selectedParticipant.id}`, bankingDetails);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async AddBankingDetails(bankingDetails: BankingDetailsViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`merchant/AddBankingDetails`, bankingDetails);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async GetContactDetails(merchantNumber: string, participantId: string): Promise<ContactDetailsViewModel> {
    const httpResult = await this._httpService.apiGet<ContactDetailsViewModel>(`merchant/GetContactDetails`,
      {
        merchantNumber: merchantNumber,
        participantId: participantId
      });
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async UpdateContactDetails(contactDetails: ContactDetailsViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`merchant/UpdateContactDetails`, contactDetails);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async AddContactDetails(contactDetails: ContactDetailsViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`merchant/AddContactDetails`, contactDetails);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async GetCommissionDetails(merchantNumber: string): Promise<UpdateCommissionDetailsViewModel[]> {
    const httpResult = await this._httpService.apiGet<UpdateCommissionDetailsViewModel[]>(`merchant/${merchantNumber}/GetCommissionDetails`);

    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async UpdateCommissionDetails(merchantNumber: string, commissionDetails: UpdateCommissionDetailsViewModel[]): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`merchant/${merchantNumber}/UpdateCommissionDetails`, commissionDetails);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async AddCommissionDetails(merchantNumber: string, commissionDetails: UpdateCommissionDetailsViewModel[]): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`merchant/${merchantNumber}/AddCommissionDetails`, commissionDetails);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async GetVASCustomerBalanceDetails(merchantNumber: string): Promise<VASCustomerBalanceViewModel> {
    const httpResult = await this._httpService.apiGet<VASCustomerBalanceViewModel>(`merchant/GetVASDetails`,
      {
        merchantNumber: merchantNumber
      });
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async AddVASCustomerBalanceDetails(vasDetails: VASCustomerBalanceViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`merchant/AddVASDetails`, vasDetails);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async UpdateVASCustomerBalanceDetails(vasDetails: VASCustomerBalanceViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`merchant/UpdateVASDetails`, vasDetails);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async GetSinkNodeMappedFields(merchantNumber: string): Promise<SinkNodeMappedFieldsViewModel[]> {

    const httpResult = await this._httpService.apiGet<SinkNodeMappedFieldsViewModel[]>(`merchant/${merchantNumber}/GetSinkNodeMappedFields`);

    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetISOPfiSinkNode(merchantNumber: string): Promise<SinkNodeMappedFieldsViewModel> {

    const httpResult = await this._httpService.apiGet<SinkNodeMappedFieldsViewModel>(`merchant/${merchantNumber}/GetISOPfiSinkNode`);

    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return;
  }

  public async GetPFI(merchantNumber: string, sinkNode: string, mappedMerchantNumber: string): Promise<PFIViewModel> {

    const httpResult = await this._httpService.apiGet<PFIViewModel>(`merchant/${merchantNumber}/${sinkNode}/${mappedMerchantNumber}/GetPFI`);

    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return;
  }

  public async GetSinkNodeMappedFieldsByParticipantId(participantId: number): Promise<SinkNodeMappedFieldsViewModel[]> {

    const httpResult = await this._httpService.apiGet<SinkNodeMappedFieldsViewModel[]>(`merchant/${participantId}/GetSinkNodeMappedFieldsByParticipantId`);

    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetSinkNodeMappedFieldsCountByParticipantId(participantId: number): Promise<number> {

    const httpResult = await this._httpService.apiGet<number>(`merchant/${participantId}/GetSinkNodeMappedFieldsCountByParticipantId`);

    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return 0;
  }

  public async GetSinkNodeMappedFieldsCountByParticipantIdAndSinkNode(participantId: number, sinkNode: string): Promise<number> {

    const httpResult = await this._httpService.apiGet<number>(`merchant/${participantId}/${sinkNode}/GetSinkNodeMappedFieldsCountByParticipantIdAndSinkNode`);

    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return 0;
  }

  public async UpdateSinkNodeMappedFields(merchantNumber: string, sinkNodeMappedFields: SinkNodeMappedFieldsViewModel[]): Promise<CreateResult> {

    const httpResult = await this._httpService.apiPost<CreateResult>(`merchant/${merchantNumber}/UpdateSinkNodeMappedFields`, sinkNodeMappedFields);

    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async AddSinkNodeMappedFields(merchantNumber: string, sinkNodeMappedFields: SinkNodeMappedFieldsViewModel[]): Promise<CreateResult> {

    const httpResult = await this._httpService.apiPost<CreateResult>(`merchant/${merchantNumber}/AddSinkNodeMappedFields`, sinkNodeMappedFields);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

  public async deleteSinkNode(item: SinkNodeMappedFieldsViewModel, merchantNumber: string): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`merchant/${merchantNumber}/RemoveSinkNodeMappedFields`, item);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return { message: "An unknown error occured", success: false };
  }

}
